'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState } from 'react'
import {
    ArrowRight,
    Download,
    Monitor,
    CheckCircle,
    Apple,
    Copy,
    ExternalLink,
    Shield,
    Settings,
    Wifi,
    AlertCircle
} from 'lucide-react'

export default function OrchestratorSetupPage() {
    const [currentStep, setCurrentStep] = useState(1)
    const [copied, setCopied] = useState(false)

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <div className="min-h-screen bg-gray-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-xl border-b border-gray-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link href="/" className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={40}
                                height={40}
                                className="rounded-xl"
                                unoptimized
                            />
                            <span className="text-xl font-bold">
                                <span className="text-[#4ade80]">Link</span>
                                <span className="text-white">Box AI</span>
                            </span>
                        </Link>

                        <div className="hidden md:flex items-center gap-8">
                            <Link href="/features" className="text-gray-400 hover:text-white transition-colors text-sm font-medium">Features</Link>
                            <Link href="/orchestrator" className="text-[#4ade80] transition-colors text-sm font-medium">Orchestrator</Link>
                            <Link href="/orchestrator/setup" className="text-white transition-colors text-sm font-medium border-b-2 border-[#4ade80] pb-1">Setup Guide</Link>
                        </div>

                        <div className="flex items-center gap-3">
                            <Link
                                href="/login"
                                className="text-gray-300 hover:text-white transition-colors text-sm font-medium px-4 py-2"
                            >
                                Log In
                            </Link>
                            <Link
                                href="/signup"
                                className="bg-gradient-to-r from-[#4ade80] to-[#22c55e] text-gray-900 px-5 py-2 rounded-lg text-sm font-semibold hover:from-[#86efac] hover:to-[#4ade80] transition-all shadow-lg shadow-green-500/25"
                            >
                                Get Started Free
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-28 pb-12 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a5f] via-gray-950 to-[#134e4a]" />
                <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#4ade80]/10 rounded-full blur-[100px]" />

                <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-full mb-6">
                        <Settings className="w-4 h-4 text-[#4ade80]" />
                        <span className="text-gray-300 text-sm font-medium">Setup Guide</span>
                    </div>

                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
                        Set Up{' '}
                        <span className="bg-gradient-to-r from-[#4ade80] via-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
                            LinkBox Orchestrator
                        </span>
                    </h1>

                    <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
                        Follow these steps to download, install, and connect your Mac to LinkBox AI.
                        The entire setup takes less than 5 minutes.
                    </p>
                </div>
            </section>

            {/* Progress Steps */}
            <section className="py-8 bg-gray-900/50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between">
                        {[1, 2, 3, 4].map((step) => (
                            <div key={step} className="flex items-center">
                                <button
                                    onClick={() => setCurrentStep(step)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${currentStep >= step
                                        ? 'bg-[#4ade80] text-gray-900'
                                        : 'bg-gray-800 text-gray-400'
                                        }`}
                                >
                                    {currentStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                                </button>
                                {step < 4 && (
                                    <div className={`w-20 md:w-32 h-1 mx-2 rounded ${currentStep > step ? 'bg-[#4ade80]' : 'bg-gray-800'
                                        }`} />
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-between mt-3 text-sm">
                        <span className={currentStep >= 1 ? 'text-white' : 'text-gray-500'}>Download</span>
                        <span className={currentStep >= 2 ? 'text-white' : 'text-gray-500'}>Install</span>
                        <span className={currentStep >= 3 ? 'text-white' : 'text-gray-500'}>Permissions</span>
                        <span className={currentStep >= 4 ? 'text-white' : 'text-gray-500'}>Connect</span>
                    </div>
                </div>
            </section>

            {/* Step Content */}
            <section className="py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                    {/* Step 1: Download */}
                    {currentStep === 1 && (
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4ade80] to-[#22c55e] flex items-center justify-center text-white">
                                    <Download className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Step 1: Download</h2>
                                    <p className="text-gray-400">Get the latest version for macOS</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <p className="text-gray-300">
                                    Download the LinkBox Orchestrator DMG file. The app is signed and notarized by Apple
                                    for your security.
                                </p>

                                <a
                                    href="https://github.com/BB23-LLC/LinkBoxOrchestrator/releases/latest/download/LinkBoxOrchestrator.dmg"
                                    className="inline-flex items-center justify-center gap-3 bg-white text-gray-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all shadow-2xl"
                                >
                                    <Apple className="w-6 h-6" />
                                    <div className="text-left">
                                        <div className="text-xs text-gray-500">Download for</div>
                                        <div className="font-bold">macOS</div>
                                    </div>
                                    <Download className="w-5 h-5 ml-2" />
                                </a>

                                <div className="flex items-center gap-3 text-gray-400 text-sm">
                                    <Shield className="w-4 h-4 text-[#4ade80]" />
                                    <span>Signed & Notarized by Apple</span>
                                    <span className="text-gray-600">•</span>
                                    <Monitor className="w-4 h-4" />
                                    <span>macOS 13+ required</span>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-end">
                                <button
                                    onClick={() => setCurrentStep(2)}
                                    className="inline-flex items-center gap-2 bg-[#4ade80] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-[#86efac] transition-all"
                                >
                                    Next: Install
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Install */}
                    {currentStep === 2 && (
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white">
                                    <Monitor className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Step 2: Install</h2>
                                    <p className="text-gray-400">Drag to Applications folder</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <ol className="list-decimal list-inside space-y-4 text-gray-300">
                                    <li>Open the downloaded <code className="bg-gray-800 px-2 py-1 rounded text-[#4ade80]">LinkBoxOrchestrator.dmg</code> file</li>
                                    <li>Drag <strong>LinkBox Orchestrator</strong> to your <strong>Applications</strong> folder</li>
                                    <li>Open <strong>Applications</strong> folder and double-click <strong>LinkBox Orchestrator</strong></li>
                                    <li>If prompted "App is from an unidentified developer", go to <strong>System Preferences → Privacy & Security</strong> and click <strong>"Open Anyway"</strong></li>
                                </ol>

                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-amber-200 font-medium">First Launch</p>
                                            <p className="text-amber-200/70 text-sm mt-1">
                                                On first launch, macOS may show a security dialog. This is normal for apps
                                                downloaded outside the App Store. The app is fully signed and notarized.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-between">
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white px-4 py-2 transition-colors"
                                >
                                    ← Back
                                </button>
                                <button
                                    onClick={() => setCurrentStep(3)}
                                    className="inline-flex items-center gap-2 bg-[#4ade80] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-[#86efac] transition-all"
                                >
                                    Next: Permissions
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Permissions */}
                    {currentStep === 3 && (
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white">
                                    <Shield className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Step 3: Grant Permissions</h2>
                                    <p className="text-gray-400">Enable required system access</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <p className="text-gray-300">
                                    LinkBox Orchestrator needs certain permissions to control apps, access files, and use your microphone.
                                    You'll be prompted for each when you first use a feature.
                                </p>

                                <div className="space-y-4">
                                    <div className="bg-gray-800/50 rounded-lg p-4">
                                        <h3 className="text-white font-semibold mb-2">🎤 Microphone</h3>
                                        <p className="text-gray-400 text-sm">Required for voice commands. Grant when prompted.</p>
                                    </div>
                                    <div className="bg-gray-800/50 rounded-lg p-4">
                                        <h3 className="text-white font-semibold mb-2">♿ Accessibility</h3>
                                        <p className="text-gray-400 text-sm">Required for controlling apps and automation. Go to <strong>System Preferences → Privacy & Security → Accessibility</strong> and add LinkBox Orchestrator.</p>
                                    </div>
                                    <div className="bg-gray-800/50 rounded-lg p-4">
                                        <h3 className="text-white font-semibold mb-2">📁 Full Disk Access (Optional)</h3>
                                        <p className="text-gray-400 text-sm">Enables searching all files. Add in <strong>System Preferences → Privacy & Security → Full Disk Access</strong>.</p>
                                    </div>
                                    <div className="bg-gray-800/50 rounded-lg p-4">
                                        <h3 className="text-white font-semibold mb-2">🏠 HomeKit (Optional)</h3>
                                        <p className="text-gray-400 text-sm">Required for smart home control. Grant when you first try to control a device.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-between">
                                <button
                                    onClick={() => setCurrentStep(2)}
                                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white px-4 py-2 transition-colors"
                                >
                                    ← Back
                                </button>
                                <button
                                    onClick={() => setCurrentStep(4)}
                                    className="inline-flex items-center gap-2 bg-[#4ade80] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-[#86efac] transition-all"
                                >
                                    Next: Connect
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 4: Connect */}
                    {currentStep === 4 && (
                        <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white">
                                    <Wifi className="w-7 h-7" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">Step 4: Connect to LinkBox</h2>
                                    <p className="text-gray-400">Sign in with your LinkBox account</p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <p className="text-gray-300">
                                    Launch LinkBox Orchestrator and sign in with your LinkBox account credentials.
                                    Once connected, you'll see a green "Ready" status.
                                </p>

                                <ol className="list-decimal list-inside space-y-4 text-gray-300">
                                    <li>Click <strong>"Sign In"</strong> in the Orchestrator window</li>
                                    <li>Enter your LinkBox email and password (or use Apple/Google Sign-In)</li>
                                    <li>Wait for connection — you'll see <span className="text-[#4ade80] font-semibold">● Ready - Using Link Ultra Voice</span></li>
                                    <li>Try a voice command: Click the microphone or say "Hey LinkBox"</li>
                                </ol>

                                <div className="bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                        <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-[#4ade80] font-medium">You're all set!</p>
                                            <p className="text-gray-300 text-sm mt-1">
                                                Your Mac is now connected to LinkBox AI. Try saying "What can you do?" to see all available commands.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <h3 className="text-white font-semibold mb-2">📱 Connect from iOS</h3>
                                    <p className="text-gray-400 text-sm mb-3">
                                        You can also control your Mac from the LinkBox iOS app. Make sure your iPhone is on the same network,
                                        or enable port forwarding for remote access.
                                    </p>
                                    <a
                                        href="https://apps.apple.com/app/id6753836843"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-[#4ade80] hover:text-[#86efac] text-sm font-medium"
                                    >
                                        Download iOS App
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>

                            <div className="mt-8 flex justify-between">
                                <button
                                    onClick={() => setCurrentStep(3)}
                                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white px-4 py-2 transition-colors"
                                >
                                    ← Back
                                </button>
                                <Link
                                    href="/dashboard"
                                    className="inline-flex items-center gap-2 bg-[#4ade80] text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-[#86efac] transition-all"
                                >
                                    Go to Dashboard
                                    <ArrowRight className="w-5 h-5" />
                                </Link>
                            </div>
                        </div>
                    )}

                </div>
            </section>

            {/* Troubleshooting */}
            <section className="py-12 bg-gray-900/30">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-2xl font-bold text-white mb-6">Troubleshooting</h2>

                    <div className="space-y-4">
                        <details className="bg-gray-900/50 border border-gray-800 rounded-lg">
                            <summary className="px-6 py-4 cursor-pointer text-white font-medium hover:bg-gray-800/30 transition-colors">
                                "App is damaged and can't be opened"
                            </summary>
                            <div className="px-6 pb-4 text-gray-400">
                                <p className="mb-2">This happens when macOS quarantine flag is set. Run this command in Terminal:</p>
                                <code className="block bg-gray-800 p-3 rounded text-sm text-[#4ade80]">
                                    xattr -cr /Applications/LinkBox\ Orchestrator.app
                                </code>
                            </div>
                        </details>

                        <details className="bg-gray-900/50 border border-gray-800 rounded-lg">
                            <summary className="px-6 py-4 cursor-pointer text-white font-medium hover:bg-gray-800/30 transition-colors">
                                Orchestrator shows "Connecting..." but never connects
                            </summary>
                            <div className="px-6 pb-4 text-gray-400">
                                <p>Check your internet connection and firewall settings. The Orchestrator needs to reach:</p>
                                <ul className="list-disc list-inside mt-2 space-y-1">
                                    <li>*.supabase.co (authentication & database)</li>
                                    <li>*.googleapis.com (Google AI)</li>
                                </ul>
                            </div>
                        </details>

                        <details className="bg-gray-900/50 border border-gray-800 rounded-lg">
                            <summary className="px-6 py-4 cursor-pointer text-white font-medium hover:bg-gray-800/30 transition-colors">
                                Voice commands aren't working
                            </summary>
                            <div className="px-6 pb-4 text-gray-400">
                                <p>Make sure microphone permission is granted in System Preferences → Privacy & Security → Microphone.
                                    Also check that your default input device is set correctly in System Preferences → Sound → Input.</p>
                            </div>
                        </details>

                        <details className="bg-gray-900/50 border border-gray-800 rounded-lg">
                            <summary className="px-6 py-4 cursor-pointer text-white font-medium hover:bg-gray-800/30 transition-colors">
                                iOS app can't find my Mac
                            </summary>
                            <div className="px-6 pb-4 text-gray-400">
                                <p className="mb-2">Both devices must be on the same local network. If you&apos;re using a VPN, try disabling it.</p>
                                <p className="mb-1"><strong>Mac Orchestrator ports:</strong> 8085 (HTTP), 8086 (A2A WebSocket), 8084 (MCP)</p>
                                <p><strong>Windows Orchestrator ports:</strong> 8080 (HTTP), 8081 (A2A WebSocket), 8082 (MCP)</p>
                                <p className="mt-2 text-sm">Make sure your firewall allows these ports.</p>
                            </div>
                        </details>
                    </div>
                </div>
            </section>

            {/* Port Forwarding Guide */}
            <section className="py-12 bg-gray-950">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white">
                            <Wifi className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-bold text-white">Remote Access: Port Forwarding</h2>
                            <p className="text-gray-400">Control your Mac from anywhere in the world</p>
                        </div>
                    </div>

                    <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8 space-y-6">
                        <p className="text-gray-300">
                            By default, the Orchestrator only works on your local network. To control your Mac remotely
                            (from cellular data or another location), you need to set up <strong>port forwarding</strong> on your router.
                        </p>

                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-amber-200 font-medium">Security Note</p>
                                    <p className="text-amber-200/70 text-sm mt-1">
                                        Port forwarding exposes your Mac to the internet. The Orchestrator uses authentication,
                                        but for maximum security, consider using a VPN instead of port forwarding.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold text-white mb-4">Required Ports</h3>
                            <p className="text-gray-400 mb-4 text-sm">Mac and Windows use different port ranges to avoid conflicts when both are on the same network.</p>

                            {/* Mac Ports */}
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                <Apple className="w-5 h-5" /> macOS Orchestrator
                            </h4>
                            <div className="grid md:grid-cols-3 gap-4 mb-6">
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-[#4ade80]/20 flex items-center justify-center">
                                            <span className="text-[#4ade80] font-mono font-bold text-sm">8085</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">HTTP API</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">REST API, health checks, avatar serving, logs.</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-[#4ade80]/20 flex items-center justify-center">
                                            <span className="text-[#4ade80] font-mono font-bold text-sm">8086</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">A2A WebSocket</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">Real-time agent communication from iOS/Android/Web.</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-[#4ade80]/20 flex items-center justify-center">
                                            <span className="text-[#4ade80] font-mono font-bold text-sm">8084</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">MCP</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">Model Context Protocol for tool discovery.</p>
                                </div>
                            </div>

                            {/* Windows Ports */}
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                                <Monitor className="w-5 h-5" /> Windows Orchestrator
                            </h4>
                            <div className="grid md:grid-cols-3 gap-4">
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                            <span className="text-blue-400 font-mono font-bold text-sm">8080</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">HTTP API</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">REST API, health checks, avatar serving, logs.</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                            <span className="text-blue-400 font-mono font-bold text-sm">8081</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">A2A WebSocket</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">Real-time agent communication from iOS/Android/Web.</p>
                                </div>
                                <div className="bg-gray-800/50 rounded-lg p-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <div className="w-10 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                            <span className="text-blue-400 font-mono font-bold text-sm">8082</span>
                                        </div>
                                        <div>
                                            <p className="text-white font-medium">MCP</p>
                                            <p className="text-gray-400 text-xs">TCP</p>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">Model Context Protocol for tool discovery.</p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold text-white mb-4">Step 1: Find Your Mac's Local IP</h3>
                            <p className="text-gray-400 mb-3">Open Terminal and run:</p>
                            <code className="block bg-gray-800 p-3 rounded text-sm text-[#4ade80] mb-3">
                                ipconfig getifaddr en0
                            </code>
                            <p className="text-gray-400 text-sm">This will show something like <code className="bg-gray-800 px-2 py-1 rounded">192.168.1.100</code>. Write this down.</p>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold text-white mb-4">Step 2: Configure Your Router</h3>
                            <p className="text-gray-400 mb-4">
                                Log into your router's admin page (usually <code className="bg-gray-800 px-2 py-1 rounded">192.168.1.1</code> or <code className="bg-gray-800 px-2 py-1 rounded">192.168.0.1</code>)
                                and find the Port Forwarding or Virtual Server section. Add two rules:
                            </p>

                            {/* Router Screenshot */}
                            <div className="rounded-xl overflow-hidden border border-gray-700 mb-4">
                                <Image
                                    src="/router-port-forwarding.png"
                                    alt="Router port forwarding configuration showing ports 8080 and 8081"
                                    width={800}
                                    height={500}
                                    className="w-full"
                                    unoptimized
                                />
                            </div>

                            <div className="bg-gray-800/50 rounded-lg overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-800">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-gray-300">Name</th>
                                            <th className="px-4 py-3 text-left text-gray-300">External Port</th>
                                            <th className="px-4 py-3 text-left text-gray-300">Internal Port</th>
                                            <th className="px-4 py-3 text-left text-gray-300">Internal IP</th>
                                            <th className="px-4 py-3 text-left text-gray-300">Protocol</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-700">
                                        <tr className="bg-gray-800/20">
                                            <td className="px-4 py-2 text-gray-400 font-semibold text-xs" colSpan={5}>macOS</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxOrch MAC_HTTP</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8085</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8085</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your Mac IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxOrch Mac_A2A</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8086</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8086</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your Mac IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxOrch MAC_MCP</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8084</td>
                                            <td className="px-4 py-3 text-[#4ade80] font-mono">8084</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your Mac IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                        <tr className="bg-gray-800/20">
                                            <td className="px-4 py-2 text-gray-400 font-semibold text-xs" colSpan={5}>Windows</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxPC_HTTP</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8080</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8080</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your PC IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxPC_A2A</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8081</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8081</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your PC IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                        <tr>
                                            <td className="px-4 py-3 text-white">LinkBoxPC_MCP</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8082</td>
                                            <td className="px-4 py-3 text-blue-400 font-mono">8082</td>
                                            <td className="px-4 py-3 text-gray-400 font-mono">Your PC IP</td>
                                            <td className="px-4 py-3 text-gray-400">All (TCP)</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-gray-400 text-sm mt-3">
                                <strong>Note:</strong> Replace the IP placeholders with your device&apos;s actual local IP address (e.g., <code className="bg-gray-800 px-2 py-1 rounded">192.168.0.107</code> for Mac).
                            </p>

                            <div className="bg-[#4ade80]/10 border border-[#4ade80]/30 rounded-lg p-4 mt-4">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-5 h-5 text-[#4ade80] flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-[#4ade80] font-medium">Auto-Verification</p>
                                        <p className="text-gray-300 text-sm mt-1">
                                            The Orchestrator automatically verifies port forwarding on startup by testing external
                                            connectivity through your public IP. If the health endpoint responds, the status banner
                                            will turn green automatically — no manual confirmation needed.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold text-white mb-4">Step 3: Find Your Public IP</h3>
                            <p className="text-gray-400 mb-3">
                                Your public IP is what the iOS app will use to connect. Visit{' '}
                                <a href="https://whatismyip.com" target="_blank" rel="noopener noreferrer" className="text-[#4ade80] hover:underline">
                                    whatismyip.com
                                </a>
                                {' '}or run:
                            </p>
                            <code className="block bg-gray-800 p-3 rounded text-sm text-[#4ade80]">
                                curl ifconfig.me
                            </code>
                        </div>

                        <div>
                            <h3 className="text-xl font-semibold text-white mb-4">Step 4: Configure iOS App</h3>
                            <p className="text-gray-400">
                                In the LinkBox iOS app, go to <strong>Settings → Orchestrator</strong> and enter your public IP
                                address (e.g., <code className="bg-gray-800 px-2 py-1 rounded">73.xxx.xxx.xxx</code>).
                                The app will connect over the internet to your Mac.
                            </p>
                        </div>

                        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-blue-200 font-medium">Pro Tip: Use Dynamic DNS</p>
                                    <p className="text-blue-200/70 text-sm mt-1">
                                        Most home internet connections have dynamic IPs that change periodically.
                                        Use a free Dynamic DNS service like{' '}
                                        <a href="https://www.noip.com" target="_blank" rel="noopener noreferrer" className="text-blue-300 hover:underline">No-IP</a>
                                        {' '}to get a hostname that always points to your home IP.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-950 border-t border-gray-800 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-3">
                            <Image
                                src="/linkbox-logo.png"
                                alt="LinkBox AI"
                                width={32}
                                height={32}
                                className="rounded-lg"
                                unoptimized
                            />
                            <span className="text-sm text-gray-400">
                                © 2026 BB23 LLC. All rights reserved.
                            </span>
                        </div>
                        <div className="flex items-center gap-6">
                            <Link href="/" className="text-gray-400 hover:text-white text-sm transition-colors">Home</Link>
                            <Link href="/orchestrator" className="text-gray-400 hover:text-white text-sm transition-colors">Orchestrator</Link>
                            <Link href="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">Privacy</Link>
                            <Link href="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">Terms</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    )
}
